import { useEffect, useState, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { Button, DataTable } from '../../../../shared/components/index.js';

interface ApprovalRow {
  readonly documentExchangeId: string;
  readonly documentCode: string;
  readonly title: string;
  readonly documentType: string;
  readonly classification: string;
  readonly status: string;
}

const PAGE = 25;

/**
 * M42 §27 ApprovalCenter (BR-04 / §14). External approvers review documents
 * awaiting decision and approve or reject them. Viewing needs
 * M42_DOCUMENT_VIEW, deciding needs M42_DOCUMENT_APPROVE.
 */
export function ApprovalCenter(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState<ApprovalRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    params.set('status', 'PENDING_APPROVAL');
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: ApprovalRow[]; total: number }>(`/v1/portal/documents?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, page, reload]);

  const decide = async (id: string, decision: 'APPROVE' | 'REJECT'): Promise<void> => {
    setActionError(null);
    setNotice(null);
    const path = decision === 'APPROVE' ? 'approve' : 'reject';
    try {
      await apiRequest(`/v1/portal/documents/${id}/${path}`, { method: 'POST', body: { notes: `${decision} via approval center` }, token });
      setNotice(decision === 'APPROVE' ? t('m42.approval.approved') : t('m42.approval.rejected'));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m42.approval.notAuthDecide'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="approval-center">
      <h1>{t('m42.approval.title')}</h1>

      {actionError && <p role="alert" aria-label="approval-action-error">{actionError}</p>}
      {notice && <p aria-label="approval-notice">{notice}</p>}
      {error && <p role="alert" aria-label="approval-error">{error}</p>}

      {loading ? <p>{t('common.loading')}</p> : (
        <>
          <p aria-label="approval-total">{t('m42.approval.total', { count: total })}</p>
          <DataTable
            ariaLabel="approval-table"
            rows={rows}
            rowKey={(d) => d.documentExchangeId}
            emptyMessage={t('m42.approval.empty')}
            columns={[
              { key: 'code', header: t('m42.common.code'), cellAriaLabel: (d) => `approval-code-${d.documentExchangeId}`, render: (d) => d.documentCode },
              { key: 'title', header: t('m42.common.title'), render: (d) => d.title },
              { key: 'type', header: t('m42.common.type'), render: (d) => d.documentType },
              { key: 'class', header: t('m42.common.classCol'), render: (d) => d.classification },
              { key: 'actions', header: t('m42.common.actions'), render: (d) => (
                <>
                  <Button variant="secondary" type="button" aria-label={`approve-${d.documentExchangeId}`} onClick={() => void decide(d.documentExchangeId, 'APPROVE')}>{t('m42.approval.approveBtn')}</Button>
                  <Button variant="danger" type="button" aria-label={`reject-${d.documentExchangeId}`} onClick={() => void decide(d.documentExchangeId, 'REJECT')}>{t('m42.approval.rejectBtn')}</Button>
                </>
              ) },
            ]}
          />

          <nav aria-label="approval-pagination">
            <Button variant="secondary" type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>{t('m42.common.previous')}</Button>
            <span aria-label="page-indicator">{t('m42.common.pageOf', { page: page + 1, pages: Math.max(1, Math.ceil(total / PAGE)) })}</span>
            <Button variant="secondary" type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>{t('m42.common.next')}</Button>
          </nav>
        </>
      )}
    </main>
  );
}
